import { Children, ReactNode, useRef } from "react"
import { Tab as RATab, TabList as RATabList, TabPanel as RATabPanel } from "react-aria-components"
import { twMerge } from "tailwind-merge"
import { isTabItem } from "./utils"
import { SelectedTabIndicator } from "./SelectedTabIndicator"
import { Badge } from "../Badge"
import { Icon } from "../Icon"
import { TabsItemProps } from "./types"

function Tab({
  id,
  href,
  children,
  badgeContent,
  badgeProps,
  iconName,
  iconProps,
  ...rest
}: TabsItemProps) {
  return (
    <RATab
      className="grid h-full cursor-default auto-cols-min grid-flow-col items-center gap-x-2 whitespace-nowrap px-2 py-5 text-sm/5 font-normal text-gray-400 outline-none ring-primary-lighten-50 first:ps-0 hover:text-black focus-visible:text-black focus-visible:ring-4 aria-disabled:cursor-not-allowed aria-disabled:text-gray-300 aria-selected:font-medium aria-selected:text-primary-base"
      href={href}
      id={id}
      {...rest}
    >
      {({ isSelected, isDisabled }) => (
        <>
          {children}
          {badgeContent && (
            <Badge
              className={twMerge(isDisabled && "opacity-50")}
              variation={isSelected ? "success" : "gray"}
              {...badgeProps}
            >
              {badgeContent}
            </Badge>
          )}
          {iconName && (
            <Icon
              aria-hidden
              className={twMerge(isDisabled && "opacity-50")}
              name={iconName}
              {...iconProps}
            />
          )}
        </>
      )}
    </RATab>
  )
}

export function TabsInner({ label, children }: { label: string; children: ReactNode }) {
  const tabListRef = useRef<HTMLDivElement>(null)
  const tabItems = Children.toArray(children).filter(isTabItem)

  return (
    <>
      <SelectedTabIndicator tabListRef={tabListRef}>
        <RATabList
          className="grid w-min min-w-[100%] auto-cols-min grid-flow-col grid-rows-1 items-center border-b-2 border-gray-200"
          aria-label={label}
          ref={tabListRef}
        >
          {tabItems.map(tabItem => (
            <Tab key={tabItem.props.id} {...tabItem.props} />
          ))}
        </RATabList>
      </SelectedTabIndicator>
      {tabItems.map(tabItem => {
        const { id, panel } = tabItem.props
        return (
          <RATabPanel
            className="mt-4 focus-visible:outline focus-visible:outline-black" // TODO: Make configurable for back compat?
            id={id}
            key={id}
          >
            {panel}
          </RATabPanel>
        )
      })}
    </>
  )
}
